import { API_BASE_URL, buildApiUrl } from './config';

export type Audiobook = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  content?: string | null;
  ai_summary?: string | null;
  cover?: string | null;
  audio_file?: string | null;
  duration?: number | null;
  transcript?: string | null;
  publisher?: string | null;
  released_at?: string | null;
  language?: string | null;
  read_url?: string | null;
  rating_average?: number | null;
  rating_count?: number | null;
  views?: number | null;
  is_featured?: boolean;
  categories?: AudiobookCategory[];
  topics?: AudiobookTopic[];
  people?: AudiobookPerson[];
  tags?: unknown[];
  chapters?: AudiobookChapter[];
  created_at?: string;
  updated_at?: string;
};

export type AudiobookChapter = {
  id: number;
  audiobook_id: number;
  name: string;
  description?: string | null;
  cover?: string | null;
  audio_file?: string | null;
  duration?: number | null;
  order?: number | null;
  transcript?: string | null;
  ai_summary?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type AudiobookCategory = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  image?: string | null;
  parent_id?: number | null;
};

export type AudiobookTopic = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  image?: string | null;
  icon?: string | null;
  order?: number | null;
  is_featured?: boolean;
};

export type AudiobookPerson = {
  id: number;
  name: string;
  slug: string;
  role?: 'author' | 'narrator' | 'both' | string;
  role_label?: string | null;
  pivot_role?: 'author' | 'narrator' | 'both' | string | null;
  order?: number | null;
  description?: string | null;
  bio?: string | null;
  avatar?: string | null;
};

export type MobileHomeSection = {
  id?: number;
  name?: string;
  source_type?: 'category' | 'topic' | 'featured' | 'latest' | 'popular' | string;
  source_id?: number | null;
  category?: AudiobookCategory | null;
  topic?: AudiobookTopic | null;
  audiobooks: Audiobook[];
};

export type MobileHomeData = {
  continue_listening: Audiobook[];
  featured: Audiobook[];
  latest: Audiobook[];
  popular: Audiobook[];
  topics: AudiobookTopic[];
  categories: AudiobookCategory[];
  narrators: AudiobookPerson[];
  sections: MobileHomeSection[];
};

export type PersonDetailData = {
  person: AudiobookPerson;
  audiobooks: Audiobook[];
};

type ApiEnvelope<T> = {
  data: T;
};

type RawPersonDetailData = {
  person?: AudiobookPerson;
  audiobooks?: Audiobook[] | { data?: Audiobook[] };
  data?: {
    person?: AudiobookPerson;
    audiobooks?: Audiobook[] | { data?: Audiobook[] };
  };
};

async function request<T>(path: string, params?: Record<string, string | number | boolean | undefined>) {
  const url = buildApiUrl(API_BASE_URL, path, params);

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${path}`);
  }

  const json = await response.json() as ApiEnvelope<T> | T;
  return 'data' in Object(json) ? (json as ApiEnvelope<T>).data : json as T;
}

function normalizeList<T>(value: T[] | { data?: T[] }) {
  if (Array.isArray(value)) {
    return value;
  }

  return Array.isArray(value.data) ? value.data : [];
}

function normalizePersonDetail(value: RawPersonDetailData): PersonDetailData {
  const source = value.data?.person ? value.data : value;

  return {
    person: source.person as AudiobookPerson,
    audiobooks: normalizeList<Audiobook>(source.audiobooks ?? []),
  };
}

export function getMobileHome(limit = 10, profileId?: number | null) {
  return request<MobileHomeData>('/audiobook-mobile/home', { limit, profile_id: profileId || undefined });
}

export function getAudiobooks(params?: {
  per_page?: number;
  order_by?: 'created_at' | 'updated_at' | 'name' | 'duration' | 'views';
  order?: 'asc' | 'desc';
  category_id?: number;
  topic_id?: number;
  person_id?: number;
  featured?: 1;
  search?: string;
}) {
  return request<Audiobook[] | { data?: Audiobook[] }>('/audiobooks', params).then(normalizeList);
}

export function searchAudiobooks(q: string, perPage = 10) {
  return request<Audiobook[] | { data?: Audiobook[] }>('/audiobooks/search', { q, per_page: perPage }).then(normalizeList);
}

export function getAudiobookBySlug(slug: string) {
  return request<Audiobook>(`/audiobooks/${slug}`);
}

export function getAudiobookChapters(id: number) {
  return request<AudiobookChapter[] | { data?: AudiobookChapter[] }>(`/audiobooks/${id}/chapters`).then(normalizeList);
}

export function getCategories() {
  return request<AudiobookCategory[] | { data?: AudiobookCategory[] }>('/audiobook-categories').then(normalizeList);
}

export function getTopics() {
  return request<AudiobookTopic[] | { data?: AudiobookTopic[] }>('/audiobook-topics').then(normalizeList);
}

export function getPeople(params?: { per_page?: number; page?: number }) {
  return request<AudiobookPerson[] | { data?: AudiobookPerson[] }>('/audiobook-people', params).then(normalizeList);
}

export function getPlayerDetail(id: number) {
  return request<Audiobook>(`/audiobook-mobile/player/${id}`);
}

export function getPersonDetail(id: number, perPage = 10) {
  return request<RawPersonDetailData>(`/audiobook-people/${id}`, { per_page: perPage }).then(normalizePersonDetail);
}

export function getSuggestions(params?: {
  category_ids?: string;
  topic_ids?: string;
  person_ids?: string;
  per_page?: number;
}) {
  return request<Audiobook[] | { data?: Audiobook[] }>('/audiobook-mobile/suggestions', params).then(normalizeList);
}
